import React, { useEffect, useState } from 'react'
import { Redirect } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import flowRight from 'lodash/flowRight'
import get from 'lodash/get'

import { Loading } from '@fielded/shared-ui'
import { getShipmentBatches } from '@fielded/fs-api/lib/shipment/utils/utils'
import { SHIPMENT_STATUS } from '@fielded/fs-api/lib/shipment/constants'

import { withApi } from '../../../../../common/ApiProvider'
import withConfig from '../../../../../van-shared/hoc/withConfig'
import { withUser } from '../../../../../common/AuthenticationProvider'
import withShipment from '../../../common/WithShipmentWrapper'
import { fetchMasterData, selectMasterData } from '../../../../../root/reducers/shipments/master-data'
import { isPsmResellerApp, isPsmResellerFacilityUser, hasSkipValidationComment } from '../../../common/utils'
import { useAmplitudeAnalytics } from '../../../../../common/analytics-tracking/AmplitudeAnalyticsProvider'
import FacilityDeliveryDetail from './FacilityDeliveryDetail'

const FacilityDeliveryDetailContainer = ({
  api,
  user,
  config,
  history,
  shipment: delivery
}) => {
  const dispatch = useDispatch()
  const masterData = useSelector(selectMasterData)
  const trackingHandler = useAmplitudeAnalytics()

  const [otp, setOtp] = useState()
  const [loading, setLoading] = useState(true)

  const isPsmResellerFacility = isPsmResellerFacilityUser(config, user)
  const hasReceivedMasterData = get(masterData, 'hasReceivedMasterData', false)
  const shipmentId = get(delivery, 'id')
  const snapshotId = get(delivery, 'snapshotId')

  const isDeliveryReceived = delivery.status === SHIPMENT_STATUS.RECEIVED
  const skipToPickList = delivery.status === SHIPMENT_STATUS.ARRIVED
  const pickListUrl = `/shipments/pick-list/${snapshotId}`

  const onConfirmDelivery = async () => {
    // When a facility user confirms delivery, add a 'skip-validation' comment
    // to indicate that validation was intentionally bypassed
    if (isPsmResellerApp(config) && !hasSkipValidationComment(delivery)) {
      const comment = 'skip-validation'
      await api.shipment.addComment(snapshotId, comment)
    }

    trackingHandler.shipment.started({ shipment: delivery })
    history.push(pickListUrl)
  }

  const initialize = async () => {
    try {
      if (!hasReceivedMasterData) {
        await dispatch(fetchMasterData())
      }

      const otp = await api.shipment.otp.rest.getPassword(shipmentId)
      const otpCode = otp ? otp.password : ''

      setOtp(otpCode)
      trackingHandler.shipment.viewed({ shipment: delivery })
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    initialize()
  }, [])

  if (skipToPickList) {
    return <Redirect to={pickListUrl} />
  }

  if (loading) {
    return <Loading loadingText='Loading delivery details ...' />
  }

  if (!masterData || !Object.keys(masterData).length || !delivery) {
    return <Redirect to='/shipments/facility/deliveries' />
  }

  const productsById = get(masterData, 'products.byId')
  const batches = getShipmentBatches(delivery, productsById)

  return (
    <FacilityDeliveryDetail
      otp={otp}
      history={history}
      delivery={delivery}
      products={batches}
      isPsmResellerFacility={isPsmResellerFacility}
      isDeliveryReceived={isDeliveryReceived}
      snapshotId={snapshotId}
      onConfirmDelivery={onConfirmDelivery}
    />
  )
}

const withHOCs = flowRight(
  withApi,
  withConfig,
  withUser,
  withShipment
)

export default withHOCs(FacilityDeliveryDetailContainer)
